import React, { useState, useEffect } from 'react';
import styles from './teamEditor.module.css';
import { showErrorToast, showInfoToast } from '../../../../code/ToastHelper';
import { Icons } from '../../../../code/IconHandler';
import PersonSelection from '../../ToolComponents/PersonSelection/PersonSelection';
import axios from 'axios';
import { EditorState, convertToRaw, convertFromRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import AppointmentEditor from '../AppointmentEditor/AppointmentEditor';

export default function TeamEditor({ initialData, categories, onClose }) {
    const [form, setForm] = useState({
        name: initialData?.name || '',
        url_slug: initialData?.url_slug || '',
        category_id: `${initialData?.category_id}` || '',
        news_api: initialData?.news_api || '',
        fupa_team_embed: initialData?.fupa_team_embed || '',
        fupa_table_embed: initialData?.fupa_table_embed || '',
        fussballde_matches_embed: initialData?.fussballde_matches_embed || '',
        football_cat: initialData?.football_cat || 'Senioren',
        junioren_cat: initialData?.junioren_cat || '',
        current_league: initialData?.current_league,
        team_img: initialData?.team_img,
        team_img_preview: ''
    });
    const [deleteOldImage, setDeleteOldImage] = useState(false);
    const [teamImageFile, setTeamImageFile] = useState(null);
    const [adminOpen, setAdminOpen] = useState(false);
    const [assignments, setAssignments] = useState(initialData?.assignments || []);
    const [allPersons, setAllPersons] = useState([]);
    const [appointments, setAppointments] = useState(initialData?.appointments || []);
    const [information, setInformation] = useState(
        initialData?.information
            ? EditorState.createWithContent(convertFromRaw(JSON.parse(initialData?.information)))
            : EditorState.createEmpty()
    );
    const token = localStorage.getItem('token');
    useEffect(() => {
        fetchAllPersons();
    }, []);
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Just save selected file, no upload yet
        setTeamImageFile(file);

        // Optional: Show preview using URL.createObjectURL(file)
        setForm(f => ({ ...f, team_img_preview: URL.createObjectURL(file) }));
    };

    const handleImageDelete = async (filename) => {
        if (!filename) return;

        try {
            const res = await fetch(`/api/upload/delete-team-image/${filename}`, {
                method: 'DELETE'
            });

            if (!res.ok) throw new Error('Delete failed');

            showInfoToast('Bild gelöscht');
            setForm(f => ({ ...f, team_img: null }));
        } catch (err) {
            console.error(err);
            showErrorToast('Fehler beim Löschen des Bildes');
        }
    };
    async function fetchAllPersons() {
        try {
            axios.get('/api/contact', {
                headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
                const contacts = res.data;
                setAllPersons(contacts);

                if (initialData?.assignments) {
                    const assignmentsWithContacts = initialData.assignments.map(a => ({
                        ...a,
                        contact: contacts.find(c => c.id === a.contact.id) || null,
                    }));
                    setAssignments(assignmentsWithContacts);
                }
            });
        } catch {
            showErrorToast('Fehler beim Laden der Personen');
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
    };

    const addContact = (contact) => {
        setAssignments(prev => [
            ...prev,
            {
                id: null, // Not persisted yet
                contact,
                position: '',
                aufgabe: '',
                show_email: true,
                show_phone: true,
            }
        ]);
    };

    const removeContact = (contactId) => {
        setAssignments(prev => prev.filter(a => a.contact.id !== contactId));
    };

    const updatePosition = (contactId, newPosition) => {
        setAssignments(prev => prev.map(a =>
            a.contact && a.contact.id === contactId ? { ...a, position: newPosition } : a
        ));
    };

    const updateAufgabe = (contactId, newAufgabe) => {
        setAssignments(prev => prev.map(a =>
            a.contact && a.contact.id === contactId ? { ...a, aufgabe: newAufgabe } : a
        ));
    };

    const toggleEmailVisibility = (contactId) => {
        setAssignments(prev => prev.map(a =>
            a.contact && a.contact.id === contactId ? { ...a, show_email: !a.show_email } : a
        ));
    };

    const togglePhoneVisibility = (contactId) => {
        setAssignments(prev => prev.map(a =>
            a.contact && a.contact.id === contactId ? { ...a, show_phone: !a.show_phone } : a
        ));
    };
    function isEditorEmpty(editorState) {
        const content = editorState.getCurrentContent();
        const blocks = content.getBlocksAsArray();
        return blocks.length === 1 && blocks[0].getText().trim() === "";
    }

    const handleSubmit = async () => {

        try {

            let newImagePath = form.team_img; // current image path

            // If user chose a new file, upload it first
            if (teamImageFile) {
                const formData = new FormData();
                formData.append('image', teamImageFile);

                const uploadRes = await fetch('/api/upload/upload-team-image', {
                    method: 'POST',
                    body: formData
                });

                if (!uploadRes.ok) throw new Error('Upload failed');
                const uploadData = await uploadRes.json();

                newImagePath = uploadData.path;

                // Delete old image if needed
                if (form.team_img && form.team_img !== 'default_team.svg' && !deleteOldImage) {
                    await handleImageDelete(form.team_img);
                }
            }

            // If user only wants to delete the old image without new upload
            if (deleteOldImage && !teamImageFile && form.team_img && form.team_img !== 'default_team.svg') {
                await handleImageDelete(form.team_img);
                newImagePath = null;
            }

            const payload = {
                name: form.name,
                url_slug: form.url_slug,
                category_id: form.category_id,
                news_api: form.news_api,
                fupa_team_embed: form.fupa_team_embed,
                fupa_table_embed: form.fupa_table_embed,
                fussballde_matches_embed: form.fussballde_matches_embed,
                football_cat: form.football_cat || 'Senioren',
                junioren_cat: form.junioren_cat || '',
                team_img: newImagePath,
                current_league: form.current_league,
                information: isEditorEmpty(information)
                    ? null
                    : JSON.stringify(convertToRaw(information.getCurrentContent())),
                appointments: appointments.map(app => ({
                    ...app,
                    id: app.id || null,
                    topic_type: 'team',
                })),
                assignments: assignments.map(a => ({
                    id: a.id || null,
                    contact_id: a.contact.id,
                    position: a.position,
                    aufgabe: a.aufgabe,
                    show_email: a.show_email,
                    show_phone: a.show_phone,
                })),

            };


            const token = localStorage.getItem('token');
            const method = initialData ? 'PUT' : 'POST';
            const url = initialData ? `/api/team/${initialData.id}` : `/api/team`;

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) throw new Error('Failed to save team');
            showInfoToast(initialData ? `${initialData.name} wurde geupdated` : `${form.name} wurde erstellt!`)
            onClose(true);
        } catch (e) {
            console.error(e);
            showErrorToast('Fehler beim Speichern.');
        }
    };

    const toolbar = {
        options: [
            'inline',
            'blockType',
            'list',
            'textAlign',
            'link',
            'emoji',
            'history'
        ],
        blockType: {
            options: [
                'Normal', // unstyled text
                'H2',
                'H3',
            ],
        },
        inline: {

            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
        },
        list: {

            options: ['unordered', 'ordered', 'indent', 'outdent'],
        },
        textAlign: {

            options: ['left', 'center', 'right', 'justify'],
        },
        link: {
            inDropdown: true,
            options: ['link', 'unlink'],
        },
        history: {
            options: ['undo', 'redo'],
        }
    }
    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className={styles.teamEditorForm}>
            <h2>{initialData ? 'Team bearbeiten' : 'Neues Team hinzufügen'}</h2>
            <Icons.X className={styles.closeButton} onClick={() => onClose(false)} />
            <label>
                Kategorie:
                <select name="category_id" value={form.category_id} onChange={handleChange} required>
                    <option value="">-- Kategorie wählen --</option>
                    {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </label>
            <label>
                Name:
                <input name="name" value={form.name} onChange={handleChange} required />
            </label>
            {form.category_id === "1" && (
                <>
                    <label>
                        Fußball-Kategorie:
                        <select name="football_cat" value={form.football_cat} onChange={handleChange} required>
                            <option value="Senioren">Senioren</option>
                            <option value="Junioren">Junioren</option>
                            <option value="Sonstige">Sonstige</option>
                        </select>
                    </label>

                    {form.football_cat === "Junioren" && (
                        <label>
                            Junioren-Unterkategorie:
                            <select name="junioren_cat" value={form.junioren_cat} onChange={handleChange} required>
                                <option value="">-- wählen --</option>
                                <option value="Talentwerk">Talentwerk</option>
                                <option value="Breitensport">Breitensport</option>
                            </select>
                        </label>
                    )}
                </>
            )}
            <label>
                Team-Bild:
                <input type="file" accept="image/*" onChange={handleImageUpload} />
            </label>

            {(form.team_img || form.team_img_preview) && (
                <div className={styles.imagePreview}>
                    <img
                        src={form.team_img_preview || form.team_img}
                        alt="Team"
                        style={{ maxWidth: '200px', borderRadius: '8px' }}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setDeleteOldImage(true);
                            setForm(f => ({ ...f, team_img: null, team_img_preview: null }));
                            setTeamImageFile(null);
                        }}
                    >
                        Bild löschen
                    </button>
                </div>
            )}
            <label>
                Aktuelle Liga:
                <input
                    name="current_league"
                    value={form.current_league || ''}
                    onChange={handleChange}
                    placeholder="z. B. Bezirksliga West"
                />
            </label>
            <div className={styles.selection}>
                <label className={styles.label}>
                    {form.category_id === "2" || form.category_id === "3"
                        ? 'Ansprechpartner'
                        : 'Trainer'}
                </label>
                <PersonSelection
                    persons={assignments}
                    allPersons={allPersons}
                    disableSelect={false}
                    onAddPerson={addContact}
                    onRemovePerson={removeContact}
                    onUpdatePosition={updatePosition}
                    onUpdateTask={updateAufgabe}
                    onToggleEmail={toggleEmailVisibility}
                    onTogglePhone={togglePhoneVisibility}
                />
            </div>
            <AppointmentEditor
                appointments={appointments}
                onChange={(updatedAppointments) => setAppointments(updatedAppointments)}
            />




            <label>
                Information:
                <Editor editorState={information} onEditorStateChange={setInformation} wrapperClassName={styles.editor} toolbar={toolbar} />
            </label>

            <div className={styles.adminContainer}>
                <button
                    type="button"
                    className={styles.adminToggle}
                    onClick={() => setAdminOpen(!adminOpen)}
                >
                    {adminOpen ? '🔒 Admin-Bereich schließen' : '🔓 Admin-Bereich öffnen'}
                </button>

                {adminOpen && (
                    <div className={styles.adminNote}>
                        <div className={styles.adminNoteHeader}>🔒 Admin-Bereich</div>
                        <p>Der folgende Abschnitt ist nur für Administratoren relevant. Normale Nutzer können diesen ignorieren.</p>
                        <label>
                            News API URL:
                            <input name="news_api" value={form.news_api} onChange={handleChange} />
                        </label>
                        {form?.category_id === '1' && (
                            <>
                                <label>
                                    URL Slug:
                                    <input name="url_slug" value={form.url_slug} onChange={handleChange} required />
                                </label>

                                <label>
                                    Widget: Fupa Mannschaftsübersicht
                                    <textarea className={styles.widgetInput} name="fupa_team_embed" value={form.fupa_team_embed} onChange={handleChange} />
                                </label>

                                <label>
                                    Widget: Fupa Tabelle
                                    <textarea className={styles.widgetInput} name="fupa_table_embed" value={form.fupa_table_embed} onChange={handleChange} />
                                </label>

                                <label>
                                    Widget: Fussball.de Spiele
                                    <textarea className={styles.widgetInput} name="fussballde_matches_embed" value={form.fussballde_matches_embed} onChange={handleChange} />
                                </label>
                            </>
                        )}
                    </div>
                )}
            </div>


            <div className={styles.formButtons}>
                <button type="submit">{initialData ? 'Speichern' : 'Hinzufügen'}</button>
                <button type="button" onClick={() => onClose(false)}>Abbrechen</button>
            </div>
        </form>
    );
}
